import {
  parseEventDate,
  isDateOnlyString,
  startOfLocalDay,
} from '../dateTimeUtils';

/**
 * Filter an already-expanded event list down to those whose LOCAL start date
 * matches a target day, dropping archived + cancelled occurrences. Pure:
 * `expandedEvents` is an explicit argument. Behavior is pinned by
 * `eventsForDate.char.test.ts`.
 */
export function getEventsForDate<T extends { archived?: boolean; cancelledOccurrence?: boolean; startAt: unknown }>(
  expandedEvents: T[],
  date: Date,
): T[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  return expandedEvents.filter((event) => {
    if (event.archived || event.cancelledOccurrence) return false;

    const eventDate = parseEventDate(event.startAt);
    if (!eventDate) return false;

    return eventDate.getFullYear() === year &&
           eventDate.getMonth() === month &&
           eventDate.getDate() === day;
  });
}

/**
 * Get the last calendar day an event actually occupies (date-only, local midnight).
 * All-day events use an EXCLUSIVE end date (Google/iCal convention): a one-day
 * all-day event is start=D, end=D+1, so the last occupied day is end MINUS ONE.
 * A timed event whose end falls EXACTLY on local midnight (00:00:00.000) ends at
 * the start of that day and occupies no time on it, so it's exclusive too (a
 * 9pm–midnight event is a single day; a 5-day trip ending midnight shouldn't
 * paint a 6th day). Other timed events occupy through their end day as-is.
 * Returns null when no endAt. Matches the monthLayout.ts version.
 */
export function getLastOccupiedDay(event: {
  allDay?: boolean;
  startAt: string;
  endAt?: string;
}): Date | null {
  const end = parseEventDate(event.endAt);
  if (!end) return null;
  const last = startOfLocalDay(end);
  const endsAtLocalMidnight =
    end.getHours() === 0 &&
    end.getMinutes() === 0 &&
    end.getSeconds() === 0 &&
    end.getMilliseconds() === 0;
  const isAllDayish = Boolean(event.allDay) || isDateOnlyString(event.startAt);
  // All-day endAt is stored INCLUSIVE (the last occupied day), so it IS the last
  // occupied day — no decrement. Only a TIMED event ending exactly at local
  // midnight occupies no time on that day (a 9pm–midnight event is one day; a
  // multi-day trip ending midnight shouldn't paint the next day), so it alone is
  // exclusive. (#calendar-allday-inclusive — was: all-day treated as exclusive -1)
  if (!isAllDayish && endsAtLocalMidnight) {
    last.setDate(last.getDate() - 1);
  }
  return last;
}

/**
 * Filter an already-expanded event list down to those whose time span overlaps
 * with the target day (including multi-day events), dropping archived +
 * cancelled occurrences. Used by Week view to render all-day events that span
 * multiple days.
 */
export function getEventsOverlappingDay<
  T extends {
    archived?: boolean;
    cancelledOccurrence?: boolean;
    startAt: string;
    endAt?: string;
    allDay?: boolean;
  },
>(expandedEvents: T[], date: Date): T[] {
  const dayOnly = startOfLocalDay(date).getTime();

  return expandedEvents.filter((event) => {
    if (event.archived || event.cancelledOccurrence) return false;

    const start = parseEventDate(event.startAt);
    if (!start) return false;

    const lastOccupied = getLastOccupiedDay(event);
    if (
      lastOccupied &&
      lastOccupied.getTime() > startOfLocalDay(start).getTime()
    ) {
      // Multi-day event: check if day falls within [start, lastOccupied]
      const startMs = startOfLocalDay(start).getTime();
      const endMs = lastOccupied.getTime();
      return dayOnly >= startMs && dayOnly <= endMs;
    }

    // Single-day event: check if it starts on this day
    return startOfLocalDay(start).getTime() === dayOnly;
  });
}
